export interface Results {
  version: string,
	campaign_version: "1.0.0" | "2.0.0" | "2.1.0" | "2.1.1" | "2.1.2" | "3.0.0",
  variant?: "full",
  scores: Scores,
  styles: {
    leadership?: "transformational" | "transactional" | "laissez_faire" | "unknown",
    motivation?: "intrinsic" | "extrinsic" | "task_value" | "self_efficacy",
    learning_strategy?: "rehearsal" | "elaboration" | "organization" | "critical_thinking",
  },
  badges?: {
    altruism: boolean,
    autonomy: boolean,
    boldness: boolean,
    cooperation: boolean,
    curiosity: boolean,
    willingness_to_learn: boolean,
    diplomacy: boolean,
    efficiency: boolean,
    innovation: boolean,
    leadership_adaptability: boolean,
    optimism: boolean,
    perseverance: boolean,
    humor: boolean,
    positivity: boolean,
    pragmatism: boolean,
    social_desirability: boolean,
  },
  compatibility: number,
  completed_at: string,
}

export interface Scores {
  adaptability?: number,
  self_efficacy?: number,
  active_listening?: number,
  empathy?: number,
  time_management?: number,
  stress_management?: number,
  decision_making?: number,
  problem_solving?: number,
  social_desirability?: number,
  leadership?: {
    laissez_faire?: number,
    transactional?: number,
    transformational?: number,
  },
  planning?: number,
}

type LeadershipKey = keyof NonNullable<Scores['leadership']>;
type FlatScoreKey = keyof Omit<Scores, 'leadership'>;

// Flatten a single Scores object into a simple key→value map
function flattenScores(scores: Scores): Record<string, number> {
  const { leadership, ...rest } = scores;
  const flat: Record<string, number> = {};

  for (const [key, value] of Object.entries(rest)) {
    if (value !== undefined) flat[key] = value as number;
  }

  if (leadership) {
    for (const [key, value] of Object.entries(leadership)) {
      if (value !== undefined) flat[`leadership.${key}`] = value as number;
    }
  }

  return flat;
}

// Aggregate all score lists into a map of key → number[]
function groupByKey(list: Scores[]): Record<string, number[]> {
  const groups: Record<string, number[]> = {};

  for (const scores of list) {
    for (const [key, value] of Object.entries(flattenScores(scores))) {
      (groups[key] ??= []).push(value);
    }
  }

  return groups;
}

export function averageScores(list: Scores[]): Scores {
  const groups = groupByKey(list);

  return Object.fromEntries(
    Object.entries(groups).filter(([key, values]) => values != null && key !== 'social_desirability').map(([key, values]) => [
      key,
      values.reduce((sum, v) => sum + v, 0) / values.length,
    ])
  );
}

export function minMaxScores(list: Scores[]): { min: Scores; max: Scores } {
  const groups = groupByKey(list);

  const min: Scores = {};
  const max: Scores = {};

  for (const [key, values] of Object.entries(groups)) {
    (min as any)[key] = Math.min(...values);
    (max as any)[key] = Math.max(...values);
  }

  return { min, max };
}